import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowLeft, User, Mail, Lock, Eye, EyeOff, Loader2, Shield, CheckCircle, Gift, AlertTriangle, CreditCard, Star, Rocket, Crown, Check } from 'lucide-react';
import { BackgroundOrbs, StaggerContainer, StaggerItem } from '@/components/ui/animations';

// Available plans for selection
const PLANS = [
  { 
    id: 'starter', 
    name: 'Starter', 
    price: 19, 
    icon: Rocket,
    color: 'emerald',
    features: ['1 Trading Account', '5 DCA Bots', '10 Grid Bots', '2 Signal Bots']
  },
  { 
    id: 'pro', 
    name: 'Pro', 
    price: 79, 
    icon: Star,
    color: 'cyan',
    popular: true,
    features: ['3 Trading Accounts', '20 DCA Bots', '50 Grid Bots', 'AI Strategy Builder']
  },
  { 
    id: 'power', 
    name: 'Power Trader', 
    price: 199, 
    icon: Crown,
    color: 'purple',
    features: ['Unlimited Accounts', 'Unlimited Bots', 'Priority AI Access', 'Futures + Margin']
  }
];

export default function Register({ auth }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const [trialBlocked, setTrialBlocked] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState('');
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('pro'); // Default to Pro
  const navigate = useNavigate();

  // Check trial eligibility on mount
  useEffect(() => {
    checkTrialEligibility();
  }, []);

  // Capture referral code and plan from URL on mount
  useEffect(() => {
    const ref = searchParams.get('ref');
    const plan = searchParams.get('plan');
    
    if (ref) {
      setReferralCode(ref.toUpperCase());
      sessionStorage.setItem('referral_code', ref.toUpperCase());
    } else {
      const storedRef = sessionStorage.getItem('referral_code');
      if (storedRef) {
        setReferralCode(storedRef);
      }
    }
    
    if (plan && ['starter', 'pro', 'power'].includes(plan)) {
      setSelectedPlan(plan);
    }
  }, [searchParams]);

  const checkTrialEligibility = async () => {
    try {
      const response = await axios.get(`${API}/trial/check-eligibility`);
      if (!response.data.eligible) {
        setTrialBlocked(true);
        setBlockedMessage(response.data.message || 'Free trial not available from this network.');
      }
    } catch (error) {
      console.error('Eligibility check failed:', error);
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      // Pass referral code and selected plan to registration
      await auth.register(email, password, name, referralCode || null, selectedPlan);
      toast.success(`Account created! Your 7-day free trial has started. You'll be charged $${PLANS.find(p => p.id === selectedPlan)?.price}/month after the trial.`);
      sessionStorage.removeItem('referral_code');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      if (detail?.error === 'trial_not_available') {
        setTrialBlocked(true);
        setBlockedMessage(detail.message);
        toast.error('Free trial not available. Please subscribe to continue.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    const refParam = referralCode ? `&ref=${referralCode}` : '';
    const planParam = selectedPlan ? `&plan=${selectedPlan}` : '';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}${refParam}${planParam}`;
  };

  // If trial is blocked, show upgrade prompt
  if (trialBlocked) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden">
        <BackgroundOrbs />
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        
        <div className="relative z-10 w-full max-w-md px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="backdrop-blur-xl bg-slate-900/60 border border-amber-500/30 rounded-2xl p-8"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Trial Already Used</h1>
              <p className="text-slate-400">{blockedMessage}</p>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <p className="text-sm text-slate-300 mb-2">
                  It looks like a free trial has already been used from your network. 
                  To continue using BitTrade HQ, please subscribe to one of our plans.
                </p>
              </div>
              
              <Link to="/pricing">
                <Button className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold">
                  <CreditCard className="w-5 h-5 mr-2" />
                  View Subscription Plans
                </Button>
              </Link>
              
              <Link to="/login">
                <Button variant="outline" className="w-full h-12 border-white/10 text-white hover:bg-white/5">
                  Already have an account? Sign In
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const selectedPlanData = PLANS.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden">
      {/* Animated Background */}
      <BackgroundOrbs />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20" />
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(6,182,212,0.1),transparent_50%)]" />

      <div className="relative z-10 w-full max-w-xl px-6 py-12">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-400 hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
        </motion.div>

        {/* Register Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="backdrop-blur-xl bg-slate-900/60 border border-white/10 rounded-2xl p-8"
        >
          {/* Logo */}
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center gap-3 group mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </Link>
            <h1 className="text-2xl font-bold text-white mb-1">Start Your Free Trial</h1>
            <p className="text-slate-400 text-sm">7 days free, then pick your plan</p>
          </div>

          {/* Plan Selection */}
          <div className="mb-6">
            <Label className="text-slate-300 text-sm font-medium mb-3 block">Select your plan after trial</Label>
            <div className="grid grid-cols-3 gap-3">
              {PLANS.map((plan) => {
                const Icon = plan.icon;
                const isSelected = selectedPlan === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? `border-${plan.color}-500 bg-${plan.color}-500/10` 
                        : 'border-white/10 bg-slate-800/30 hover:border-white/20'
                    }`}
                    data-testid={`plan-select-${plan.id}`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-cyan-500 text-[10px] font-bold text-white rounded-full">
                        POPULAR
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${isSelected ? `text-${plan.color}-400` : 'text-slate-400'}`} />
                      <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                        {plan.name}
                      </span>
                    </div>
                    <p className={`text-lg font-bold ${isSelected ? `text-${plan.color}-400` : 'text-white'}`}>
                      ${plan.price}<span className="text-xs font-normal text-slate-400">/mo</span>
                    </p>
                    {isSelected && (
                      <div className={`absolute top-2 right-2 w-5 h-5 bg-${plan.color}-500 rounded-full flex items-center justify-center`}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedPlanData && (
              <div className="mt-3 p-3 bg-slate-800/30 rounded-lg">
                <p className="text-xs text-slate-400 mb-2">{selectedPlanData.name} includes:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedPlanData.features.map((f, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Referral Badge */}
          {referralCode && (
            <div className="mb-4 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Gift className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-purple-300">You were referred!</p>
                <p className="text-xs text-slate-400">Code: {referralCode}</p>
              </div>
            </div>
          )}

          {/* Trial Info */}
          <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle className="w-4 h-4" />
                7 days free access to all features
              </li>
              <li className="flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle className="w-4 h-4" />
                Card charged ${selectedPlanData?.price}/mo after trial
              </li>
              <li className="flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle className="w-4 h-4" />
                Cancel anytime before trial ends
              </li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm font-medium">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 h-11 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-blue-500/20"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-11 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-blue-500/20"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="pl-10 pr-10 h-11 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-blue-500/20"
                  required
                  minLength={8}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <Checkbox 
                id="terms"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
                className="mt-0.5 border-white/20 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                data-testid="register-terms"
              />
              <label htmlFor="terms" className="text-xs text-slate-300 leading-relaxed cursor-pointer">
                I agree to the{' '}
                <Link to="/legal-documents" className="text-amber-400 hover:text-amber-300 underline">Terms of Service</Link>,{' '}
                <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link>, and{' '}
                <Link to="/legal-documents" className="text-blue-400 hover:text-blue-300 underline">Payment Terms</Link>.
                <span className="block mt-1 text-slate-500">
                  I understand I'll be charged ${selectedPlanData?.price}/month after my 7-day trial unless I cancel.
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Start 7-Day Free Trial
                </>
              )}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-900 px-2 text-slate-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full h-11 border-white/10 text-white hover:bg-white/5 font-medium"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 flex items-center justify-center gap-6 text-slate-500"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="text-xs">256-bit Encryption</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span className="text-xs">Non-Custodial</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
